'use client';

import React from 'react';
import { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { OssModal } from '../cards/oss-modal';
import { cards, type OssCard } from '../cards/card-data';
import './carousel.css';
import { AnimatedLink } from '@/components/AnimatedLink';

export function FeatureCards() {
  const [currentCard, setCurrentCard] = useState(0);
  const featuredCards = cards.filter(card => card.isFeatured) as OssCard[];
  const totalCards = featuredCards.length;

  const goToNext = () => {
    setCurrentCard(prev => (prev + 1) % totalCards);
  };

  const goToPrev = () => {
    setCurrentCard(prev => (prev - 1 + totalCards) % totalCards);
  };

  return (
    <section className="px-section py-section bg-gradient-to-r from-[#1D5360] via-[#6C4EB1] to-[#9747FF] text-gray-5">
      <div className="max-w-section">
        <div className="mb-10 flex items-center justify-between">
          <h2 className="text-h2">Featured</h2>

          <div className="flex gap-2">
            <button
              onClick={goToPrev}
              disabled={currentCard === 0}
              className="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-white/10 backdrop-blur-sm transition-all hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-30"
              aria-label="Previous card"
            >
              <ChevronLeftIcon className="size-6" />
            </button>

            <button
              onClick={goToNext}
              disabled={currentCard === totalCards - 1}
              className="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-white/10 backdrop-blur-sm transition-all hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-30"
              aria-label="Next card"
            >
              <ChevronRightIcon className="size-6" />
            </button>
          </div>
        </div>
        <div className="relative overflow-x-scroll" role="region" aria-label="Featured projects carousel">
          <div
            className="carousel-track flex gap-6 transition-transform duration-300 ease-in-out"
            style={
              {
                '--carousel-current-card': currentCard,
              } as React.CSSProperties
            }
          >
            {featuredCards.map(card => (
              <FeatureCard
                key={card.name}
                name={card.name}
                description={card.description}
                link={card.link}
                icon={card.icon}
                bgClass={card.featureBgClass!}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

type FeatureCardProps = Pick<OssCard, 'name' | 'description' | 'link' | 'icon'> & {
  bgClass: string;
};

function FeatureCard({ name, description, link, icon, bgClass }: FeatureCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const onClose = () => setIsOpen(false);

  return (
    <>
      <OssModal name={name} description={description} link={link} icon={icon} isOpen={isOpen} onClose={onClose} />
      <div className="flex w-[300px] shrink-0 flex-col gap-6 rounded-3xl bg-[#18181A] p-6 text-white md:w-[400px]">
        <div className={`text-red grid h-[200px] place-content-center rounded-2xl ${bgClass}`}>
          {React.cloneElement(icon, {
            className: 'size-20 text-white',
            monochrome: true,
          } as React.HTMLAttributes<HTMLElement> & { monochrome?: boolean })}
        </div>
        <div className="flex flex-col gap-2">
          <h3 className="text-eyebrow-strong">{name}</h3>
          {/* // make links inside description inhert. We only want to use them inside the modal */}
          <p className="text-body mb-auto line-clamp-3 text-gray-4 [&_a]:pointer-events-none [&_a]:no-underline">
            {description}
          </p>
          <p>
            <button
              className="text-body text-gray-4 underline underline-offset-4 hover:text-white"
              onClick={() => setIsOpen(true)}
            >
              Read more
            </button>
          </p>
        </div>
        <AnimatedLink href={link} linkText="Explore build data" className="text-gradle-green" hrefTarget="_blank" />
      </div>
    </>
  );
}
